import { Paintbrush } from "lucide-react";
import { Outlet } from "react-router";
import AccessDeniedPage from "@/shared/components/ErrorPage/AccessDeniedPage";
import Loading from "@/shared/components/Loading";
import PageHeader from "@/shared/components/PageHeader";
import { useCommunicationBackPath } from "@/shared/hooks/useCommunicationBackPath";
import { useOrganizationQuery } from "@/shared/hooks/useOrganizationQuery";
import { Button } from "@/shared/ui/Button";
import { Separator } from "@/shared/ui/Separator";
import { useOrganizationId } from "../hooks/useOrganizationId";
import { useParametersPageState } from "../hooks/useParametersPageState";

const ParametersLayout = () => {
  const backPath = useCommunicationBackPath();
  const { tabs, selectedTab, handleTabChange } = useParametersPageState();
  const organizationId = useOrganizationId();
  const { isLoading: loading } = useOrganizationQuery(organizationId);

  if (tabs.length === 0) {
    return <AccessDeniedPage />;
  }

  return (
    <div className="flex flex-col lg:flex-row h-content min-h-full lg:overflow-hidden">
      {/* MOBILE VIEW: PageHeader + tabs */}
      <div className="block lg:hidden">
        <PageHeader
          title="Paramètres"
          description="Gérez les paramètres de votre CSE."
          backPath={backPath}
        />
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 pb-2">
            {tabs.map((tab, index) => {
              const isStudio = tab.key === "studio";
              const isFirstNonStudio = index > 0 && tabs[index - 1].key === "studio";

              return (
                <div key={tab.key} className="contents">
                  {isFirstNonStudio && (
                    <div className="w-full">
                      <Separator className="my-1" />
                    </div>
                  )}
                  <Button
                    variant={selectedTab === tab.key ? "action" : "outline"}
                    onClick={() => handleTabChange(tab.key)}
                    className={`whitespace-nowrap flex-shrink-0 ${isStudio ? "font-medium" : ""}`}
                  >
                    {isStudio && <Paintbrush className="mr-1.5 size-4" />}
                    {tab.label}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* DESKTOP VIEW: aside (PageHeader + tabs) */}
      <aside className="w-56 pr-4 flex-col gap-2 hidden lg:flex">
        <PageHeader
          title="Paramètres"
          description="Gérez les paramètres de votre CSE."
          backPath={backPath}
        />
        <div className="flex flex-col gap-2 overflow-auto">
          {tabs.map((tab, index) => {
            const isStudio = tab.key === "studio";
            const isFirstNonStudio = index > 0 && tabs[index - 1].key === "studio";

            return (
              <div key={tab.key} className="flex flex-col gap-2">
                {isFirstNonStudio && <Separator />}
                <Button
                  variant={selectedTab === tab.key ? "action" : "outline"}
                  onClick={() => handleTabChange(tab.key)}
                  className={`justify-start border-none shadow-none ${isStudio ? "font-medium" : ""}`}
                >
                  {isStudio && <Paintbrush className="mr-1.5 size-4" />}
                  {tab.label}
                </Button>
              </div>
            );
          })}
        </div>
      </aside>

      {/* content area */}
      <div className="min-w-0 min-h-content pb-4 flex-1 px-4 lg:overflow-auto">
        {loading
          ? (
            <div className="flex items-center justify-center py-8">
              <Loading state={{ loading }} />
            </div>
          )
          : <Outlet />}
      </div>
    </div>
  );
};

export default ParametersLayout;
